import { z } from 'zod'
import type { Owner, Root } from './rootModel'
import { getFeatureFlags } from '@/shared/utils/featureFlagUtils'

export const subscriptionTypes = ['webhook'] as const
export type SubscriptionType = (typeof subscriptionTypes)[number]

const baseEvents = [
    'loan.created',
    'loan.deleted',
    'loan.updated',
    'contract.created',
    'contract.updated',
    'kyc.updated',
    'credit-limit.created',
    'credit-limit.deleted',
    'credit-limit.updated',
    'invoice.created',
    'invoice.deleted',
    'invoice.updated',
    'onboarding.created',
    'onboarding.updated',
    'onboarding-document.created',
    'onboarding-document.updated',
    'receivable.created',
    'receivable.deleted',
    'receivable.updated',
    'credit-limit.outstanding-amount.updated',
    'credit-limit.reserved-amount.updated',
    'repayment.created',
    'repayment.updated',
    'payment-reconciliation.created',
    'payment-reconciliation.updated',
    'payment-reconciliation.deleted',
    'redirection.created',
    'redirection.updated',
    'redirection.paid',
    'quote.created',
    'quote.updated',
    'repayment-method.created',
    'repayment-method.updated',
    'user-limit.created',
    'user-limit.updated',
    'user-limit.outstanding-amount.updated',
    'user-limit.reserved-amount.updated',
    'debtor.created',
    'debtor.updated',
    'repayment.deleted'
] as const

// makalu-api still accepts these deprecated events, so a subscription can hold one. Makalu does not offer them.
const deprecatedEvents = ['advance.created', 'advance.updated'] as const

const requirementEvents = [
    'requirement.created',
    'requirement.updated',
    'requirement.waived',
    'requirement.eligible-party.created',
    'requirement.eligible-party.updated',
    'requirement.eligible-party.waived'
] as const

const proofEvents = ['proof.created', 'proof.archived', 'proof.linked', 'proof.unlinked'] as const

// Mirrors `commonEventTypes` of makalu-api subscriptions/createWebhookSubscription.api.ts.
export const events = [...baseEvents, ...requirementEvents, ...proofEvents] as const
export type EventType = (typeof events)[number] | (typeof deprecatedEvents)[number]

export const getSubscribableEvents = (): readonly EventType[] =>
    getFeatureFlags().hasProofs ? events : [...baseEvents, ...requirementEvents]

export type SubscriptionGateway = Root & {
    event: EventType
    type: SubscriptionType
    options: {
        endpoint: string
        secret?: string
    }
    owner: Owner
}

export type CreateSubscriptionGateway = SubscriptionGateway

export type EditSubscriptionGateway = SubscriptionGateway

export type Subscription = SubscriptionGateway

export type CreateSubscription = Pick<Subscription, 'event' | 'options'>

export type EditSubscription = {
    subscriptionId: string
    options: {
        endpoint: string
        secret?: string
    }
}

export const editSubscriptionSchema = z.object({
    subscriptionId: z.string().min(1),
    options: z.object({
        endpoint: z.string().url().min(1),
        secret: z.union([z.literal(''), z.string().min(16)])
    })
})

export type EditSubscriptionForm = z.infer<typeof editSubscriptionSchema>

export type DeleteSubscription = { subscriptionId: Subscription['id'] }
